import {
  AfterViewInit,
  Component,
  OnDestroy,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { Store } from '@ngrx/store';
import * as fromSeriousSystem from '@serious-system';
import * as fromLayout from '../../../../layout';

/**
 * SpeechToTextListeningComponent is a UI component for visualizing audio input using a series of dynamic bars.
 * The component processes microphone input and displays an audio visualization updating in real-time.
 *
 * The bars dynamically adjust their height based on the loudness of the audio input.
 * The `isLoading` input determines whether the visualization or a loading indicator is displayed.
 * The component auto-detects the device type (mobile/desktop) to adjust the number of visualized bars.
 *
 * Implements initialization and cleanup for `AudioContext` and associated resources.
 */
@Component({
  selector: 'squadbox-speech-to-text-listening',
  imports: [fromSeriousSystem.SpinnerDirective],
  template: `
    @if (!isLoading()) {
      <div class="flex gap-0.5 items-center min-h-8">
        @for (_ of bars(); track $index; let i = $index) {
          <div
            class="w-1 min-h-1 max-h-8 rounded-full transition-[height] duration-100 ease-in-out"
            [style.height.px]="heights()[i]"
            [class]="{
              'bg-transparency-black-8': heights()[i] === MIN_H,
              'bg-primary-500': heights()[i] > MIN_H,
            }"
          ></div>
        }
      </div>
    } @else {
      <div sdSpinner></div>
    }
  `,
})
export class SpeechToTextListeningComponent
  implements AfterViewInit, OnDestroy
{
  public readonly isLoading = input<boolean>();

  public readonly MIN_H = 4; // px
  public readonly MAX_H = 32; // px
  private readonly DESKTOP_BAR_COUNT = 100;
  private readonly MOBILE_BAR_COUNT = 100;
  private readonly SILENCE = 0.05; // gate threshold
  private readonly SMOOTHING = 0.8; // WebAudio smoothing constant
  private readonly CURVE = 0.75; // <1 boosts low levels (sqrt), >1 dampens
  private readonly GAIN = 2; // amplitude multiplier
  private readonly BAR_INTERVAL = 250; // ms

  public readonly isMobile = inject<Store<fromLayout.LayoutState>>(
    Store,
  ).selectSignal(fromLayout.layoutFeature.selectIsMobile);
  private readonly barCount = computed<number>(() =>
    this.isMobile() ? this.MOBILE_BAR_COUNT : this.DESKTOP_BAR_COUNT,
  );

  public readonly bars = computed(() =>
    Array.from({ length: this.barCount() }),
  );
  public readonly heights = signal<number[]>(
    Array.from({ length: this.barCount() }, () => this.MIN_H),
  );

  private currentLoudness = 0;
  private barTimer?: ReturnType<typeof setInterval>;
  private frozenHeights: number[] = Array.from(
    { length: this.barCount() },
    () => this.MIN_H,
  );
  private mediaStream: MediaStream | undefined;
  private audio?: {
    context: AudioContext;
    analyser: AnalyserNode;
    data: Uint8Array<ArrayBuffer>;
    animationFrameId: number;
  };

  /**
   * creates an AudioContext, an AnalyserNode, and
   * starts rendering the audio data by calling the recursive render() method.
   */
  ngAfterViewInit() {
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        this.mediaStream = stream;
        const context = new AudioContext();
        const analyser = context.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = this.SMOOTHING;
        context.createMediaStreamSource(stream).connect(analyser);
        this.audio = {
          context,
          analyser,
          data: new Uint8Array(analyser.frequencyBinCount),
          animationFrameId: requestAnimationFrame(() => this.render()),
        };
        this.startBarTimer();
      })
      .catch((err: Error) =>
        console.error('Failed to access microphone:', err.message || err),
      );
  }

  /**
   * Renders the audio visualization frame, processes frequency data, calculates loudness,
   * and schedules the next animation frame for continuous rendering.
   **/
  private render() {
    if (!this.audio) return;

    const { analyser, data } = this.audio;
    analyser.getByteFrequencyData(data);

    // Calculate overall loudness (RMS)
    let sum = 0;
    for (const value of data) sum += value * value;
    const rms = Math.sqrt(sum / data.length);
    this.currentLoudness = rms / 255; // Normalize 0-1

    this.audio.animationFrameId = requestAnimationFrame(() => this.render());
  }

  /**
   * Starts the bar timer that periodically updates the bar heights based on the current loudness.
   * The timer continuously adjusts an array of bar heights and updates the relevant data structures.
   */
  private startBarTimer() {
    clearInterval(this.barTimer);

    this.barTimer = setInterval(() => {
      const newHeight = this.computeBarHeight();
      this.advanceHeights(newHeight);
    }, this.BAR_INTERVAL);
  }

  private computeBarHeight(): number {
    const loud = this.currentLoudness;
    // Ensure "new" bars added after listening starts always get primary color by avoiding exact MIN_H
    if (loud <= this.SILENCE) return this.MIN_H + 0.001;

    const eased = Math.min(1, Math.pow(loud, this.CURVE) * this.GAIN);
    return Math.min(this.MAX_H, this.MIN_H + eased * (this.MAX_H - this.MIN_H));
  }

  private advanceHeights(newHeight: number) {
    const targetLen = this.barCount();
    // Ensure buffer matches current bar count
    if (this.frozenHeights.length !== targetLen) {
      this.frozenHeights = Array.from({ length: targetLen }).map(
        () => this.MIN_H,
      );
    }

    // Shift left and append the new height
    this.frozenHeights = [...this.frozenHeights.slice(1), newHeight];

    // Push into the reactive signal
    this.heights.set(this.frozenHeights.slice());
  }

  ngOnDestroy(): void {
    if (this.audio) {
      cancelAnimationFrame(this.audio.animationFrameId);
      void this.audio.context.close();
    }
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => {
        track.stop();
      });
      this.mediaStream = undefined;
    }
    if (this.barTimer) {
      clearInterval(this.barTimer);
    }
  }
}
